const STATIC_PAGE_IDS = new Set([
  "/",
  "/auth/login",
  "/extension",
  "/files",
  "/chats",
  "/meetings",
  "/meetings/new",
  "/agents",
  "/dashboards",
]);

const DYNAMIC_PAGE_IDS = [
  {
    pattern: /^\/chats\/[^/]+$/,
    pageId: "/chats/:id",
  },
  {
    pattern: /^\/meetings\/[^/]+$/,
    pageId: "/meetings/:id",
  },
  {
    pattern: /^\/agents\/[^/]+$/,
    pageId: "/agents/:agentId",
  },
  {
    pattern: /^\/share\/meetings\/[^/]+$/,
    pageId: "/share/meetings/:token",
  },
] as const;

const PAGE_TITLES: Record<string, string> = {
  "/": "Sozo AI Agent",
  "/auth/login": "ログイン | Sozo AI Agent",
  "/chats/:id": "依頼 | Sozo AI Agent",
  "/extension": "拡張機能 | Sozo AI Agent",
  "/files": "ファイル | Sozo AI Agent",
  "/chats": "依頼一覧 | Sozo AI Agent",
  "/meetings": "会議 | Sozo AI Agent",
  "/meetings/:id": "会議 | Sozo AI Agent",
  "/meetings/new": "会議を追加 | Sozo AI Agent",
  "/agents": "エージェント | Sozo AI Agent",
  "/dashboards": "ダッシュボード | Sozo AI Agent",
  "/agents/:agentId": "エージェント | Sozo AI Agent",
  "/share/meetings/:token": "共有された会議 | Sozo AI Agent",
};

// The stock RUM HTTP plugin records the complete request URL. Keep its
// automatic events to exact, static application routes; dynamic opaque IDs
// are correlated separately as API families with x-request-id.
const SAFE_STATIC_API_PATHS = [
  "/api/conversations",
  "/api/conversations/activity",
  "/api/credit-pricing",
  "/api/credit-pricing/versions",
  "/api/credit-usage",
  "/api/crm/actions",
  "/api/crm/deals",
  "/api/feedback",
  "/api/files",
  "/api/health",
  "/api/integrations",
  "/api/integrations/mazrica",
  "/api/me",
  "/api/meetings",
  "/api/organizations",
  "/api/prompts",
  "/api/recall-bots",
  "/api/reference-candidates",
  "/api/summary-templates",
  "/api/telemetry/projection-observations",
  "/api/telemetry/recording-events",
  "/api/transcription-terms",
  "/api/uploads",
  "/api/users",
] as const;
const SENSITIVE_RELATIVE_API_URLS = [
  /^\/api\/auth(?:\/|$)/,
  /^\/api\/public\/meeting-links(?:\/|$)/,
  /^\/api\/files\/[^/?#]+(?:\/|$)/,
  /^\/api\/conversations\/[^/?#]+\/workspace\/files(?:\/|$)/,
  // The client error beacon itself: a blocked report must not become RUM
  // traffic or an api_correlation network_error.
  /^\/api\/telemetry\/client-errors(?:\/|$)/,
] as const;
const SAFE_API_FAMILIES = new Set([
  "admin",
  "conversations",
  "credits",
  "crm",
  "feedback",
  "files",
  "generated-images",
  "health",
  "integrations",
  "internal",
  "me",
  "meetings",
  "organizations",
  "prompts",
  "recall-bots",
  "reference-candidates",
  "settings",
  "summary-templates",
  "telemetry",
  "transcription-terms",
  "uploads",
  "users",
]);

function normalizePathname(pathname: string) {
  const withoutQueryOrHash = pathname.split(/[?#]/, 1)[0] ?? "/";
  if (withoutQueryOrHash === "/") return "/";
  return withoutQueryOrHash.replace(/\/+$/, "") || "/";
}

/**
 * Returns only route templates known by the application. Unknown paths are
 * collapsed so user-provided slugs and public share tokens cannot become page
 * identifiers.
 */
export function toSafeRumPageId(pathname: string) {
  const normalized = normalizePathname(pathname);
  if (STATIC_PAGE_IDS.has(normalized)) return normalized;

  for (const route of DYNAMIC_PAGE_IDS) {
    if (route.pattern.test(normalized)) return route.pageId;
  }

  return "/_other";
}

export function getSafeRumPageTitle(pageId: string) {
  if (PAGE_TITLES[pageId]) return PAGE_TITLES[pageId];
  return "Sozo AI Agent";
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The RUM HTTP plugin records URLs verbatim. Limit it to same-origin API URLs
 * without a query or fragment so tokens and search parameters are never sent.
 */
export function buildSafeApiUrlPatterns(origin: string) {
  const normalizedOrigin = origin.replace(/\/+$/, "");
  const routes = SAFE_STATIC_API_PATHS.map(escapeRegExp).join("|");
  return [
    new RegExp(`^(?:${routes})$`),
    new RegExp(`^${escapeRegExp(normalizedOrigin)}(?:${routes})$`),
  ];
}

export function buildSensitiveApiUrlPatterns(origin: string) {
  const normalizedOrigin = origin.replace(/\/+$/, "");
  return SENSITIVE_RELATIVE_API_URLS.flatMap((pattern) => {
    const absolutePattern = pattern.source.replace(
      "^\\/api",
      `^${escapeRegExp(normalizedOrigin)}\\/api`,
    );
    return [pattern, new RegExp(absolutePattern)];
  });
}

export function isSafeApiUrl(url: string, origin: string) {
  try {
    const expectedOrigin = new URL(origin).origin;
    const parsed = new URL(url, expectedOrigin);
    if (
      parsed.origin !== expectedOrigin ||
      !parsed.pathname.startsWith("/api")
    ) {
      return false;
    }
    if (parsed.pathname !== "/api" && !parsed.pathname.startsWith("/api/")) {
      return false;
    }
    return !SENSITIVE_RELATIVE_API_URLS.some((pattern) =>
      pattern.test(parsed.pathname),
    );
  } catch {
    return false;
  }
}

export function toSafeRumApiFamily(url: string, origin: string) {
  if (!isSafeApiUrl(url, origin)) return "/api/_excluded";
  try {
    const parsed = new URL(url, origin);
    const family = parsed.pathname.split("/")[2];
    return family && SAFE_API_FAMILIES.has(family)
      ? `/api/${family}`
      : "/api/_other";
  } catch {
    return "/api/_other";
  }
}
